import { type ComputedRef, nextTick, ref } from 'vue';

import { STORIES_CONFIG } from '@/constants';
import matomoHelper from '@/helper/matomo';
import { useStoriesStore } from '@/stores/stories';
import type { Story } from '@/types/stories';

export function useStoriesSwiper(
  story: ComputedRef<Story | null>,
  progressBars: any,
  resetAllProgressBars: () => void,
  startProgressBarAnimation: (
    progressBarElement: HTMLElement,
    duration: number,
    resetDelay: number,
  ) => void,
  setSlideState: (
    slideIndex: number,
    duration: number,
    startTime: number,
  ) => void,
  resetPauseState: () => void,
) {
  const swiperRef = ref<any>(null);
  let currentSlideTimeout: any = null;

  const cleanupSlideTimeout = () => {
    if (currentSlideTimeout) {
      clearTimeout(currentSlideTimeout);
      currentSlideTimeout = null;
    }
  };

  const advanceToNextSlide = () => {
    if (swiperRef.value) {
      swiperRef.value.slideNext(STORIES_CONFIG.SWIPER_TRANSITION_SPEED_MS);
    }
  };

  const scheduleSlideTransition = (duration: number): number => {
    return setTimeout(() => {
      advanceToNextSlide();
    }, duration);
  };

  const initializeProgressBar = (
    slideIndex: number,
    duration: number,
    resetDelay: number,
  ) => {
    nextTick(() => {
      const progressBarElement = progressBars.value[slideIndex];
      if (progressBarElement) {
        startProgressBarAnimation(progressBarElement, duration, resetDelay);
        setSlideState(slideIndex, duration, Date.now());
      }

      currentSlideTimeout = scheduleSlideTransition(duration);
    });
  };

  const resetSlideState = () => {
    cleanupSlideTimeout();
    resetPauseState();
    resetAllProgressBars();
  };

  const startSlide = (slideIndex: number) => {
    cleanupSlideTimeout();
    resetSlideState();

    if (!story.value?.children?.[slideIndex]) {
      return;
    }

    const slideItem = story.value.children[slideIndex];

    useStoriesStore().markStorySeen(slideItem.id);

    matomoHelper.trackEvent({
      e_c: 'Stories',
      e_a: 'View Story Slide',
      e_n: slideItem.headline,
      e_v: 1,
    });

    const duration =
      slideItem.slide_duration * STORIES_CONFIG.MILLISECONDS_PER_SECOND;

    initializeProgressBar(
      slideIndex,
      duration,
      STORIES_CONFIG.PROGRESS_BAR_RESET_DELAY_MS,
    );
  };

  const setupSlideChangeHandlers = (swiperInstance: any) => {
    swiperInstance.on('slideChangeTransitionStart', () => {
      resetSlideState();
    });

    swiperInstance.on('slideChangeTransitionEnd', () => {
      setTimeout(() => {
        startSlide(swiperInstance.realIndex);
      }, STORIES_CONFIG.SLIDE_CHANGE_DELAY_MS);
    });
  };

  const initializeSwiper = (swiperInstance: any) => {
    swiperRef.value = swiperInstance;
    startSlide(swiperInstance.realIndex);
    setupSlideChangeHandlers(swiperInstance);
  };

  const cleanup = () => {
    cleanupSlideTimeout();
    resetPauseState();
  };

  const getCurrentSlideTimeout = () => currentSlideTimeout;

  const setCurrentSlideTimeout = (timeoutId: number | null) => {
    currentSlideTimeout = timeoutId;
  };

  return {
    swiperRef,
    initializeSwiper,
    startSlide,
    cleanup,
    cleanupSlideTimeout,
    getCurrentSlideTimeout,
    setCurrentSlideTimeout,
    scheduleSlideTransition,
  };
}
